/**
 * Shared parameter segment builder.
 * Used by BOTH TranslationGenerator (protocol page) and translationTemplate (add-step preview).
 *
 * Handles all scalar field types uniformly:
 *   text / number / date / user / file / linkEntity → FieldSegment (empty box for mandatory+empty)
 *   textNumber / units                              → FieldSegment + unit text
 *   dropdown                                        → DropdownSegment (rendered by SegmentRenderer)
 *   range                                           → FieldSegment with "min – max" text + optional unit text
 *
 * Does NOT handle: group / table / gradientProgram — those are caller-specific.
 */

import type { ParameterDefinition } from '@/graph/domain/types';
import type { TranslationSegment, FieldType } from './translation.types';

// ─── Shared Value Utilities ─────────────────────────────────────────────────

export function isRequired(value: unknown): boolean {
  if (value === true) return true;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    return v === 'true' || v === 'mandatory' || v === 'required' || v === '1';
  }
  if (typeof value === 'number') return value === 1;
  return false;
}

export function isEmptyValue(raw: unknown): boolean {
  if (raw == null) return true;
  if (typeof raw === 'string') return raw.trim() === '';
  if (typeof raw === 'number') return false;
  if (Array.isArray(raw)) return raw.length === 0;
  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (('parameterId' in obj || 'parameterName' in obj) && 'value' in obj) {
      return isEmptyValue(obj.value);
    }
    if ('minValue' in obj || 'maxValue' in obj || 'min' in obj || 'max' in obj) {
      const min = obj.minValue ?? obj.min;
      const max = obj.maxValue ?? obj.max;
      const minEmpty = min == null || String(min).trim() === '';
      const maxEmpty = max == null || String(max).trim() === '';
      return minEmpty && maxEmpty;
    }
    if ('amount' in obj) return isEmptyValue(obj.amount);
    if ('value' in obj) {
      return isEmptyValue(obj.value);
    }
    if (Object.keys(obj).length === 0) return true;
    return Object.values(obj).every((v) => isEmptyValue(v));
  }
  return false;
}

export function parseUnitsValue(raw: unknown, fallbackUnit = ''): { amount: string | number; unit: string } {
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;

    const isParamWrapper = ('parameterId' in obj || 'parameterName' in obj) && 'value' in obj;
    if (isParamWrapper) {
      return parseUnitsValue(obj.value, fallbackUnit);
    }

    if ('amount' in obj) {
      const nested = parseUnitsValue(obj.amount, fallbackUnit);
      const amount = nested.amount;
      const unit = String(obj.units ?? obj.unit ?? nested.unit ?? fallbackUnit);
      return { amount, unit };
    }

    if ('value' in obj) {
      const nested = parseUnitsValue(obj.value, fallbackUnit);
      const amount = nested.amount;
      const unit = String(obj.units ?? obj.unit ?? nested.unit ?? fallbackUnit);
      return { amount, unit };
    }

    const primitive = extractPrimitive(obj);
    if (primitive !== undefined && primitive !== '') {
      return {
        amount: primitive,
        unit: String(obj.units ?? obj.unit ?? fallbackUnit),
      };
    }
  }
  if (typeof raw === 'number') return { amount: raw, unit: fallbackUnit };
  if (typeof raw === 'string') return { amount: raw, unit: fallbackUnit };
  return { amount: '', unit: fallbackUnit };
}

function extractPrimitive(raw: unknown): string | number | undefined {
  if (raw == null) return undefined;
  if (typeof raw === 'string' || typeof raw === 'number') return raw;
  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (('parameterId' in obj || 'parameterName' in obj) && 'value' in obj) {
      const nested = extractPrimitive(obj.value);
      if (nested !== undefined) return nested;
    }
    if (obj.label != null && obj.label !== '') return String(obj.label);
    if (obj.value != null && obj.value !== '') {
      const nested = extractPrimitive(obj.value);
      if (nested !== undefined) return nested;
    }
    if (obj.amount != null && obj.amount !== '') {
      const nested = extractPrimitive(obj.amount);
      if (nested !== undefined) return nested;
    }
    if (obj.input != null && obj.input !== '') {
      const nested = extractPrimitive(obj.input);
      if (nested !== undefined) return nested;
    }
  }
  return undefined;
}

function extractUnit(raw: unknown): string {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return '';
  const obj = raw as Record<string, unknown>;
  if (typeof obj.units === 'string' && obj.units.trim() !== '') return obj.units;
  if (typeof obj.unit === 'string' && obj.unit.trim() !== '') return obj.unit;
  if (obj.value && typeof obj.value === 'object') {
    const nested = extractUnit(obj.value);
    if (nested) return nested;
  }
  if (obj.amount && typeof obj.amount === 'object') {
    const nested = extractUnit(obj.amount);
    if (nested) return nested;
  }
  return '';
}

export function getCanonicalValue(raw: unknown): unknown {
  const primitive = extractPrimitive(raw);
  if (primitive !== undefined) return primitive;
  if (typeof raw === 'string' || typeof raw === 'number') return raw;
  return undefined;
}

/** Format a range-type parameter value for display. */
export function formatRangeDisplay(raw: unknown, def: ParameterDefinition): string {
  // First try to extract the primitive value (handles wrapped objects)
  const primitive = extractPrimitive(raw);
  
  // If we got a valid primitive value (number or string number), display it
  if (primitive !== undefined && primitive !== '') {
    if (typeof primitive === 'number') return String(primitive);
    if (typeof primitive === 'string') {
      const trimmed = primitive.trim();
      if (trimmed !== '') {
        const parsed = Number(trimmed);
        if (Number.isFinite(parsed)) return String(parsed);
        return trimmed;
      }
    }
  }
  
  // Fallback: check for old range object format (minValue/maxValue)
  if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>;
    if ('value' in obj && obj.value && typeof obj.value === 'object') {
      return formatRangeDisplay(obj.value, def);
    }
    const hasMin = 'minValue' in obj || 'min' in obj;
    const hasMax = 'maxValue' in obj || 'max' in obj;
    if (hasMin || hasMax) {
      const min = obj.minValue ?? obj.min ?? '—';
      const max = obj.maxValue ?? obj.max ?? '—';
      return `${min} – ${max}`;
    }
  }
  
  // Value is empty: show definition acceptance bounds as a hint
  const hasMin = def.rangeMin != null;
  const hasMax = def.rangeMax != null;
  if (hasMin || hasMax) {
    return `${hasMin ? def.rangeMin : '—'} – ${hasMax ? def.rangeMax : '—'}`;
  }
  return '— – —';
}

export function mapFieldType(fieldType: string): FieldType {
  switch (fieldType) {
    case 'number': return 'number';
    case 'date': return 'date';
    case 'user': return 'user';
    case 'file': return 'file';
    case 'linkEntity': return 'link-entity';
    case 'units': return 'units';
    case 'dropdown': return 'dropdown';
    case 'range': return 'range';
    case 'textNumber': return 'textNumber';
    default: return 'text';
  }
}

// ─── Main Builder ────────────────────────────────────────────────────────────

export interface ScalarSegmentOptions {
  disabled: boolean;
  suppressUnitsPlaceholder?: boolean;
}

/**
 * Builds [label text, value segment(s)] for a single scalar parameter.
 *
 * Returns:
 *   - TranslationSegment[] when the param should be shown
 *   - null when param should be skipped (non-mandatory + empty value)
 *
 * Callers are responsible for inserting ", " separators between params.
 * Does NOT handle group / table / gradientProgram.
 */
export function buildScalarParamSegments(
  def: ParameterDefinition,
  rawValue: unknown,
  opts: ScalarSegmentOptions,
): TranslationSegment[] | null {
  const mandatory = isRequired(def.required);
  const canonicalValue = getCanonicalValue(rawValue);
  const canonicalEmpty =
    canonicalValue == null ||
    (typeof canonicalValue === 'string' && canonicalValue.trim() === '');
  const metadataOnlyObject =
    rawValue != null &&
    typeof rawValue === 'object' &&
    !Array.isArray(rawValue) &&
    canonicalEmpty &&
    def.fieldType !== 'units' &&
    def.fieldType !== 'textNumber' &&
    def.fieldType !== 'range' &&
    def.fieldType !== 'dropdown';
  const empty = isEmptyValue(rawValue) || metadataOnlyObject;
  const placeholder = '________';

  // Non-mandatory with no value → skip entirely
  if (empty && !mandatory) {
    return null;
  }

  const label = def.parameterName;
  const segs: TranslationSegment[] = [];

  
  // ── Dropdown ──────────────────────────────────────────────────────────────
  if (def.fieldType === 'dropdown') {
    segs.push({ kind: 'text', value: `${label} ` });
    const val = getCanonicalValue(rawValue);
    const options = (def.dropDownList ?? [])
      .map((d: any) => {
        if (typeof d === 'string') return d;
        return String(d?.label ?? d?.id ?? '');
      })
      .filter((o: string) => o.trim() !== '');
    segs.push({
      kind: 'dropdown',
      paramName: def.parameterName,
      value: String(val ?? ''),
      options,
      disabled: opts.disabled,
    });
    return segs;
  }

  // ── Range ─────────────────────────────────────────────────────────────────
  if (def.fieldType === 'range') {
    segs.push({ kind: 'text', value: `${label} ` });
    segs.push({
      kind: 'field',
      paramName: def.parameterName,
      value: empty ? '' : formatRangeDisplay(rawValue, def),
      fieldType: 'text',
      disabled: opts.disabled,
      style: { minWidth: '40px', maxWidth: 'fit-content' },
    });
    const rangeUnit = extractUnit(rawValue);
    if (rangeUnit) segs.push({ kind: 'text', value: ` ${rangeUnit}` });
    return segs;
  }
  

  // ── Units / textNumber ────────────────────────────────────────────────────
// ── Units / textNumber ────────────────────────────────────────────────────
if (def.fieldType === 'units' || def.fieldType === 'textNumber') {
  const fallback = def.parameterTags?.[0] ?? '';
  const parsed = parseUnitsValue(rawValue, fallback);

  segs.push({ kind: 'text', value: `${label} ` });

  const { amount, unit } = parsed;

  const isNumeric =
    typeof amount === 'number' ||
    (typeof amount === 'string' &&
      amount.trim() !== '' &&
      !Number.isNaN(Number(amount)));

  segs.push({
    kind: 'field',
    paramName: def.parameterName,
    value: empty ? '' : String(amount ?? ''),
    fieldType:
      def.fieldType === 'units'
        ? 'number'
        : (isNumeric ? 'number' : 'text'),
    disabled: opts.disabled,
    style: { minWidth: '40px', maxWidth: 'fit-content' },
  });

  if (unit) {
    segs.push({ kind: 'text', value: ` ${unit}` });
  }

  return segs;
}

  // ── Default: text / number / date / user / file / link-entity ─────────────
  segs.push({ kind: 'text', value: `${label} ` });
  const val = canonicalValue;
  const mappedFieldType = mapFieldType(def.fieldType);
  let normalizedFieldType: FieldType = mappedFieldType;
  let normalizedValue: unknown = (val ?? '');
  if (mappedFieldType === 'number') {
    if (typeof val === 'number') {
      normalizedValue = val;
    } else if (typeof val === 'string') {
      const trimmed = val.trim();
      if (trimmed === '') {
        normalizedValue = '';
      } else {
        const parsed = Number(trimmed);
        if (Number.isFinite(parsed)) {
          normalizedValue = parsed;
        } else {
          // Fall back to text rendering so non-numeric legacy values do not appear as blank number inputs.
          normalizedFieldType = 'text';
          normalizedValue = trimmed;
        }
      }
    } else {
      normalizedValue = '';
    }
  }
  segs.push({
    kind: 'field',
    paramName: def.parameterName,
    value: normalizedValue,
    fieldType: normalizedFieldType,
    disabled: opts.disabled,
    style: { minWidth: '40px', maxWidth: 'fit-content' },
  });
  const fallbackUnit = extractUnit(rawValue);
  if (fallbackUnit) segs.push({ kind: 'text', value: ` ${fallbackUnit}` });
  return segs;
}
